import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Spinner } from './Spinner';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
  fullWidth?: boolean;
  children: ReactNode;
}

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-hover',
  secondary: 'bg-surface text-content border border-line hover:bg-primary-soft',
  ghost: 'bg-transparent text-primary hover:bg-primary-soft',
  danger: 'bg-danger text-white hover:brightness-110',
};

const SIZES: Record<Size, string> = {
  /*
   * El tamano pequeno mide 44px en movil y 36px a partir de 640px.
   *
   * Es el tamano de las acciones de fila y de la paginacion. En un raton, 36px
   * sobran; con el pulgar, no: 44px es el minimo que exige `UI_STANDARDS.md` y
   * el que evita pulsar «Eliminar» queriendo pulsar «Editar». Mantener 36px en
   * escritorio conserva la densidad de las tablas, que es lo que hace util una
   * lista larga.
   */
  sm: 'h-11 px-3 text-sm sm:h-9',
  // 44px de alto: el minimo comodo para el panel del chofer, que se usa a pie de obra.
  md: 'h-11 px-4 text-sm',
  lg: 'h-12 px-5 text-base',
};

export function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  fullWidth = false,
  disabled,
  className = '',
  children,
  type = 'button',
  ...rest
}: ButtonProps): ReactNode {
  return (
    <button
      type={type}
      disabled={disabled ?? isLoading}
      aria-busy={isLoading}
      className={[
        'inline-flex items-center justify-center gap-2 rounded-card font-medium',
        'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        fullWidth ? 'w-full' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {isLoading ? <Spinner size="sm" /> : null}
      {children}
    </button>
  );
}
